/**
 * Client-side "Remember me" credentials for /login.
 * Stored in a dedicated cookie so values survive logout localStorage/sessionStorage clears.
 * Do not use localStorage or auth/token keys for these credentials.
 */

const REMEMBER_ME_COOKIE = 'spliyt_remember_me';
const REMEMBER_ME_DAYS = 30;

const getCookie = (name) => {
    try {
        const encodedName = encodeURIComponent(name);
        const parts = String(document.cookie || '').split(';');
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i].trim();
            if (part.startsWith(`${encodedName}=`)) {
                return decodeURIComponent(part.substring(encodedName.length + 1));
            }
        }
    } catch (e) {
        // ignore cookie read errors
    }
    return null;
};

const setCookie = (name, value, days) => {
    try {
        const expires = new Date(Date.now() + days * 86400000).toUTCString();
        const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
    } catch (e) {
        // ignore cookie write errors
    }
};

const deleteCookie = (name) => {
    try {
        const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${secure}`;
    } catch (e) {
        // ignore cookie delete errors
    }
};

const readRememberMePayload = () => {
    const raw = getCookie(REMEMBER_ME_COOKIE);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.isRemember !== true) {
            return null;
        }
        return {
            isRemember: true,
            username: String(parsed.username || ''),
            password: String(parsed.password ?? ''),
        };
    } catch (e) {
        return null;
    }
};

export function isRememberMeEnabled() {
    return readRememberMePayload() != null;
}

/** Snapshot before logout storage clear; cookies already survive, kept for existing logout callers. */
export function captureRememberMeCredentials() {
    const payload = readRememberMePayload();
    if (!payload) {
        return null;
    }
    return {
        isRemember: 'true',
        username: payload.username,
        password: payload.password,
    };
}

/** Restore after logout storage clear. Cookies are independent of localStorage. */
export function restoreRememberMeCredentials(snapshot) {
    if (!snapshot || snapshot.isRemember !== 'true') {
        return;
    }
    saveRememberMeCredentials(snapshot.username, snapshot.password);
}

export function saveRememberMeCredentials(username, password) {
    setCookie(REMEMBER_ME_COOKIE, JSON.stringify({
        isRemember: true,
        username: String(username || '').trim(),
        password: String(password ?? ''),
    }), REMEMBER_ME_DAYS);
}

export function clearRememberMeCredentials() {
    deleteCookie(REMEMBER_ME_COOKIE);
}

/** Prefill values for the login form. */
export function loadRememberMeForForm() {
    const payload = readRememberMePayload();
    if (!payload) {
        return { isRemember: false, username: '', password: '' };
    }
    return payload;
}
